import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MessageCircle,
  Send,
  Reply,
  TrendingUp,
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  X,
  Facebook,
  Instagram,
  Clock,
  ExternalLink,
  Settings,
  Hash,
  Activity,
} from 'lucide-react';
import { Card, CardContent } from '@/components/common/Card';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { Spinner } from '@/components/common/Spinner';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import {
  socialCaptureService,
  type SocialPage,
  type SocialKeywordRule,
  type SocialInteraction,
  type SocialPageStats,
} from '@/services/social-capture.service';

const PAGE_SIZE = 25;

const INPUT_CLASS =
  'w-full h-10 px-3 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:shadow-[3px_3px_0px_#1a1a2e] outline-none transition-colors';
const TEXTAREA_CLASS =
  'w-full px-3 py-2.5 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:shadow-[3px_3px_0px_#1a1a2e] outline-none transition-colors min-h-[100px] resize-none';

const ACTIVITY_FILTERS = ['all', 'dm_sent', 'replied', 'no_reply'] as const;
type ActivityFilter = (typeof ACTIVITY_FILTERS)[number];

const filterLabels: Record<ActivityFilter, string> = {
  all: 'All',
  dm_sent: 'DMs Sent',
  replied: 'Got Reply',
  no_reply: 'No Reply',
};

function getRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getStatusIndicator(interaction: SocialInteraction): { icon: string; label: string } {
  if (!interaction.dm_sent) return { icon: '\u26AA', label: 'Skipped' };
  if (interaction.converted) return { icon: '\uD83D\uDFE2', label: 'Converted' };
  if (interaction.conversation_id) return { icon: '\uD83D\uDFE2', label: 'Replied' };
  if (interaction.dm_sent && !interaction.conversation_id) return { icon: '\uD83D\uDFE1', label: 'Waiting' };
  return { icon: '\uD83D\uDD34', label: 'No Reply' };
}

function getWeekAgoISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString();
}

interface KeywordFormData {
  keyword: string;
  dm_text: string;
  comment_reply_enabled: boolean;
  comment_reply_variations: string[];
}

const emptyKeywordForm: KeywordFormData = {
  keyword: '',
  dm_text: '',
  comment_reply_enabled: false,
  comment_reply_variations: [],
};

export const SocialCaptureDetailPage: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();

  // Page data
  const [page, setPage] = useState<SocialPage | null>(null);
  const [loading, setLoading] = useState(true);

  // Stats
  const [stats, setStats] = useState<SocialPageStats>({
    total_comments: 0,
    total_dms: 0,
    total_replies: 0,
    total_conversions: 0,
    total_revenue: 0,
  });

  // Keywords
  const [keywords, setKeywords] = useState<SocialKeywordRule[]>([]);
  const [keywordModalOpen, setKeywordModalOpen] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState<SocialKeywordRule | null>(null);
  const [keywordForm, setKeywordForm] = useState<KeywordFormData>(emptyKeywordForm);
  const [keywordSaving, setKeywordSaving] = useState(false);
  const [deletingKeywordId, setDeletingKeywordId] = useState<string | null>(null);

  // Activity
  const [activity, setActivity] = useState<SocialInteraction[]>([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(0);
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');
  const [activityLoading, setActivityLoading] = useState(false);

  // Settings
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    daily_dm_limit: 100,
    dedup_enabled: true,
    cooldown_seconds: 300,
    trigger_post_comments: true,
    trigger_story_replies: false,
    trigger_live_comments: false,
    trigger_ad_comments: false,
  });
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Load page data + keywords + stats
  useEffect(() => {
    if (!pageId) return;
    setLoading(true);
    Promise.all([
      socialCaptureService.getPage(pageId),
      socialCaptureService.getKeywords(pageId),
      socialCaptureService.getStats(pageId, getWeekAgoISO()),
    ])
      .then(([pageData, keywordsData, statsData]) => {
        setPage(pageData);
        setKeywords(keywordsData);
        setStats(statsData);
        setSettingsForm({
          daily_dm_limit: pageData.daily_dm_limit,
          dedup_enabled: pageData.dedup_enabled,
          cooldown_seconds: pageData.cooldown_seconds,
          trigger_post_comments: pageData.trigger_post_comments,
          trigger_story_replies: pageData.trigger_story_replies,
          trigger_live_comments: pageData.trigger_live_comments,
          trigger_ad_comments: pageData.trigger_ad_comments,
        });
      })
      .catch(() => toast.error('Failed to load page data'))
      .finally(() => setLoading(false));
  }, [pageId]);

  // Load activity
  const fetchActivity = useCallback(async () => {
    if (!pageId) return;
    setActivityLoading(true);
    try {
      const result = await socialCaptureService.getActivity(pageId, {
        filter: activityFilter !== 'all' ? activityFilter : undefined,
        limit: PAGE_SIZE,
        offset: activityPage * PAGE_SIZE,
      });
      setActivity(result.data);
      setActivityTotal(result.meta.totalItems);
    } catch {
      toast.error('Failed to load activity');
    } finally {
      setActivityLoading(false);
    }
  }, [pageId, activityFilter, activityPage]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  useEffect(() => {
    setActivityPage(0);
  }, [activityFilter]);

  // Toggle page active
  const handleToggleActive = async () => {
    if (!page || !pageId) return;
    try {
      const updated = await socialCaptureService.updatePage(pageId, {
        is_active: !page.is_active,
      });
      setPage(updated);
      toast.success(updated.is_active ? 'Page activated' : 'Page paused');
    } catch {
      toast.error('Failed to update page status');
    }
  };

  // Keyword CRUD
  const openAddKeyword = () => {
    setEditingKeyword(null);
    setKeywordForm(emptyKeywordForm);
    setKeywordModalOpen(true);
  };

  const openEditKeyword = (kw: SocialKeywordRule) => {
    setEditingKeyword(kw);
    setKeywordForm({
      keyword: kw.keyword,
      dm_text: kw.dm_text,
      comment_reply_enabled: kw.comment_reply_enabled,
      comment_reply_variations: [...kw.comment_reply_variations],
    });
    setKeywordModalOpen(true);
  };

  const handleSaveKeyword = async () => {
    if (!pageId) return;
    if (!keywordForm.keyword.trim() || !keywordForm.dm_text.trim()) {
      toast.error('Keyword and DM message are required');
      return;
    }
    setKeywordSaving(true);
    try {
      if (editingKeyword) {
        const updated = await socialCaptureService.updateKeyword(pageId, editingKeyword.id, {
          keyword: keywordForm.keyword.trim(),
          dm_text: keywordForm.dm_text.trim(),
          comment_reply_enabled: keywordForm.comment_reply_enabled,
          comment_reply_variations: keywordForm.comment_reply_variations.filter((v) => v.trim()),
        });
        setKeywords((prev) => prev.map((k) => (k.id === updated.id ? updated : k)));
        toast.success('Keyword updated');
      } else {
        const created = await socialCaptureService.createKeyword(pageId, {
          keyword: keywordForm.keyword.trim(),
          dm_text: keywordForm.dm_text.trim(),
          comment_reply_enabled: keywordForm.comment_reply_enabled,
          comment_reply_variations: keywordForm.comment_reply_variations.filter((v) => v.trim()),
        });
        setKeywords((prev) => [...prev, created]);
        toast.success('Keyword created');
      }
      setKeywordModalOpen(false);
    } catch {
      toast.error('Failed to save keyword');
    } finally {
      setKeywordSaving(false);
    }
  };

  const handleDeleteKeyword = async (keywordId: string) => {
    if (!pageId) return;
    setDeletingKeywordId(keywordId);
    try {
      await socialCaptureService.deleteKeyword(pageId, keywordId);
      setKeywords((prev) => prev.filter((k) => k.id !== keywordId));
      toast.success('Keyword deleted');
    } catch {
      toast.error('Failed to delete keyword');
    } finally {
      setDeletingKeywordId(null);
    }
  };

  // Settings save
  const handleSaveSettings = async () => {
    if (!pageId) return;
    setSettingsSaving(true);
    try {
      const updated = await socialCaptureService.updatePage(pageId, settingsForm);
      setPage(updated);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  // Variation helpers
  const addVariation = () => {
    setKeywordForm((prev) => ({
      ...prev,
      comment_reply_variations: [...prev.comment_reply_variations, ''],
    }));
  };

  const updateVariation = (index: number, value: string) => {
    setKeywordForm((prev) => ({
      ...prev,
      comment_reply_variations: prev.comment_reply_variations.map((v, i) => (i === index ? value : v)),
    }));
  };

  const removeVariation = (index: number) => {
    setKeywordForm((prev) => ({
      ...prev,
      comment_reply_variations: prev.comment_reply_variations.filter((_, i) => i !== index),
    }));
  };

  const activityTotalPages = Math.ceil(activityTotal / PAGE_SIZE);

  const PlatformIcon = page?.platform === 'instagram' ? Instagram : Facebook;
  const platformColor =
    page?.platform === 'instagram'
      ? 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400'
      : 'bg-blue-600';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <button onClick={() => navigate('/social-capture')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-center py-24">
          <p className="text-gray-500">Page not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* ================================================================ */}
      {/* SECTION 1: Header + Stats Strip                                  */}
      {/* ================================================================ */}
      <div>
        <button
          onClick={() => navigate('/social-capture')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Social Capture
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0',
                platformColor
              )}
            >
              <PlatformIcon className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1a1a2e]">{page.page_name}</h1>
              {page.page_username && (
                <p className="text-sm text-gray-400">@{page.page_username}</p>
              )}
            </div>
            <Badge
              variant="outline"
              className={cn(
                'text-xs capitalize ml-2',
                page.platform === 'instagram'
                  ? 'bg-purple-50 text-purple-700'
                  : 'bg-blue-50 text-blue-700'
              )}
            >
              {page.platform}
            </Badge>
          </div>

          <button
            onClick={handleToggleActive}
            className={cn(
              'relative w-12 h-6 rounded-full transition-colors flex-shrink-0',
              page.is_active ? 'bg-emerald-500' : 'bg-gray-300'
            )}
            title={page.is_active ? 'Active - click to pause' : 'Paused - click to activate'}
          >
            <span
              className={cn(
                'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                page.is_active ? 'translate-x-6' : 'translate-x-0.5'
              )}
            />
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Comments', value: stats.total_comments, icon: MessageCircle, color: 'text-blue-600' },
          { label: 'DMs Sent', value: stats.total_dms, icon: Send, color: 'text-violet-600' },
          { label: 'Replies', value: stats.total_replies, icon: Reply, color: 'text-emerald-600' },
          { label: 'Conversions', value: stats.total_conversions, icon: TrendingUp, color: 'text-pink-600' },
        ].map((stat) => (
          <div
            key={stat.label}
            className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4"
          >
            <div className="flex items-center gap-2 mb-1">
              <stat.icon className={cn('w-4 h-4', stat.color)} />
              <span className="text-xs text-gray-500 uppercase tracking-wide">{stat.label}</span>
            </div>
            <p className="text-2xl font-bold text-[#1a1a2e] tabular-nums">{stat.value.toLocaleString()}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">This week</p>
          </div>
        ))}
      </div>

      {/* ================================================================ */}
      {/* SECTION 2: Keywords List                                         */}
      {/* ================================================================ */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Hash className="w-5 h-5 text-[#1a1a2e]" />
            <h2 className="text-lg font-semibold text-[#1a1a2e]">Keywords</h2>
            <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
              {keywords.length}
            </span>
          </div>
          <button
            onClick={openAddKeyword}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#1a1a2e] rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_#1a1a2e] transition-all"
          >
            <Plus className="w-4 h-4" />
            Add Keyword
          </button>
        </div>

        {keywords.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
            <Hash className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No keywords yet. Add one to start capturing comments.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {keywords.map((kw) => (
              <div
                key={kw.id}
                className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm font-bold text-[#1a1a2e]">{kw.keyword}</span>
                      {!kw.is_active && (
                        <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-500">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                      DM: {kw.dm_text}
                    </p>
                    {kw.comment_reply_enabled && kw.comment_reply_variations.length > 0 && (
                      <p className="text-[11px] text-gray-400">
                        Reply: {kw.comment_reply_variations[0]}
                        {kw.comment_reply_variations.length > 1 &&
                          ` (+${kw.comment_reply_variations.length - 1} more)`}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-[11px] text-gray-400">
                      <span>{kw.total_triggered} triggered</span>
                      <span>{kw.total_dms_sent} DMs</span>
                      <span>{kw.total_conversions} conversions</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => openEditKeyword(kw)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-[#1a1a2e] hover:bg-gray-100 transition-colors"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteKeyword(kw.id)}
                      disabled={deletingKeywordId === kw.id}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Keyword Add/Edit Modal */}
      <Modal
        isOpen={keywordModalOpen}
        onClose={() => setKeywordModalOpen(false)}
        title={editingKeyword ? 'Edit Keyword' : 'Add Keyword'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Keyword</label>
            <input
              type="text"
              value={keywordForm.keyword}
              onChange={(e) => setKeywordForm((prev) => ({ ...prev, keyword: e.target.value }))}
              placeholder="e.g. price, interested, info"
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DM Message</label>
            <textarea
              value={keywordForm.dm_text}
              onChange={(e) => setKeywordForm((prev) => ({ ...prev, dm_text: e.target.value }))}
              placeholder="The message sent via DM when this keyword is detected..."
              className={TEXTAREA_CLASS}
            />
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={keywordForm.comment_reply_enabled}
                onChange={(e) =>
                  setKeywordForm((prev) => ({
                    ...prev,
                    comment_reply_enabled: e.target.checked,
                  }))
                }
                className="w-4 h-4 rounded border-gray-300 text-[#1a1a2e] focus:ring-[#1a1a2e]"
              />
              <span className="text-sm text-gray-700">Also reply to the comment</span>
            </label>
          </div>

          {keywordForm.comment_reply_enabled && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Comment Reply Variations
                </label>
                <button
                  onClick={addVariation}
                  className="text-xs text-[#1a1a2e] hover:underline flex items-center gap-0.5"
                >
                  <Plus className="w-3 h-3" /> Add variation
                </button>
              </div>
              {keywordForm.comment_reply_variations.length === 0 && (
                <p className="text-xs text-gray-400 mb-2">
                  No variations yet. Add at least one reply text.
                </p>
              )}
              <div className="space-y-2">
                {keywordForm.comment_reply_variations.map((variation, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={variation}
                      onChange={(e) => updateVariation(idx, e.target.value)}
                      placeholder={`Reply variation ${idx + 1}`}
                      className={INPUT_CLASS}
                    />
                    <button
                      onClick={() => removeVariation(idx)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button variant="outline" size="sm" onClick={() => setKeywordModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveKeyword}
              isLoading={keywordSaving}
            >
              {editingKeyword ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ================================================================ */}
      {/* SECTION 3: Activity Feed                                         */}
      {/* ================================================================ */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-5 h-5 text-[#1a1a2e]" />
          <h2 className="text-lg font-semibold text-[#1a1a2e]">Activity</h2>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {ACTIVITY_FILTERS.map((filter) => (
            <button
              key={filter}
              onClick={() => setActivityFilter(filter)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-xl border-2 transition-colors',
                activityFilter === filter
                  ? 'border-[#1a1a2e] bg-[#1a1a2e] text-white shadow-[3px_3px_0px_#1a1a2e]'
                  : 'border-[#1a1a2e] bg-white text-gray-600 hover:bg-gray-50'
              )}
            >
              {filterLabels[filter]}
            </button>
          ))}
        </div>

        <Card className="!transform-none !shadow-[3px_3px_0px_#1a1a2e] hover:!shadow-[3px_3px_0px_#1a1a2e]">
          <CardContent className="p-0">
            {activityLoading ? (
              <div className="flex items-center justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : activity.length === 0 ? (
              <div className="text-center py-16">
                <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground font-medium">No activity yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Activity will appear here when comments are captured
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-[#1a1a2e]/10 bg-gray-50/50">
                      <th className="text-left font-medium text-muted-foreground px-4 py-3">Time</th>
                      <th className="text-left font-medium text-muted-foreground px-4 py-3">User</th>
                      <th className="text-left font-medium text-muted-foreground px-4 py-3">Comment</th>
                      <th className="text-left font-medium text-muted-foreground px-4 py-3">Keyword</th>
                      <th className="text-center font-medium text-muted-foreground px-4 py-3">Actions</th>
                      <th className="text-center font-medium text-muted-foreground px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {activity.map((item) => {
                      const status = getStatusIndicator(item);
                      const isClickable = !!item.conversation_id;
                      return (
                        <tr
                          key={item.id}
                          onClick={
                            isClickable
                              ? () => navigate(`/conversations/${item.conversation_id}`)
                              : undefined
                          }
                          className={cn(
                            'hover:bg-gray-50 transition-colors',
                            isClickable && 'cursor-pointer'
                          )}
                        >
                          <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {getRelativeTime(item.created_at)}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="min-w-0">
                              <p className="text-gray-900 text-xs font-medium truncate max-w-[140px]">
                                {item.commenter_name || item.commenter_username || 'Unknown'}
                              </p>
                              {item.commenter_username && item.commenter_name && (
                                <p className="text-[11px] text-gray-400 truncate">
                                  @{item.commenter_username}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-gray-600 truncate max-w-[200px]">
                              {item.comment_text}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            {item.matched && item.keyword_matched ? (
                              <Badge
                                variant="outline"
                                className="text-[10px] bg-violet-50 text-violet-700"
                              >
                                {item.keyword_matched}
                              </Badge>
                            ) : (
                              <span className="text-[10px] text-gray-400">No match</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-2 text-xs">
                              <span
                                className={cn(
                                  'flex items-center gap-0.5',
                                  item.comment_replied ? 'text-emerald-600' : 'text-gray-300'
                                )}
                                title={item.comment_replied ? 'Comment replied' : 'No reply'}
                              >
                                Reply {item.comment_replied ? '\u2713' : '\u2717'}
                              </span>
                              <span
                                className={cn(
                                  'flex items-center gap-0.5',
                                  item.dm_sent ? 'text-emerald-600' : 'text-gray-300'
                                )}
                                title={item.dm_sent ? 'DM sent' : 'No DM'}
                              >
                                DM {item.dm_sent ? '\u2713' : '\u2717'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <span className="text-xs">{status.icon}</span>
                              <span className="text-[10px] text-gray-500">{status.label}</span>
                              {isClickable && (
                                <ExternalLink className="w-3 h-3 text-gray-300 ml-1" />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {activityTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <span className="text-sm text-muted-foreground">
                  {activityTotal} interaction{activityTotal !== 1 ? 's' : ''}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={activityPage === 0}
                    onClick={() => setActivityPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {activityPage + 1} / {activityTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={activityPage >= activityTotalPages - 1}
                    onClick={() => setActivityPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ================================================================ */}
      {/* SECTION 4: Settings (collapsible)                                */}
      {/* ================================================================ */}
      <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]">
        <button
          onClick={() => setSettingsOpen((prev) => !prev)}
          className="w-full flex items-center justify-between p-4"
        >
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#1a1a2e]" />
            <h2 className="text-lg font-semibold text-[#1a1a2e]">Settings</h2>
          </div>
          {settingsOpen ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {settingsOpen && (
          <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
            {/* DM Limit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Daily DM Limit
              </label>
              <input
                type="number"
                min={1}
                value={settingsForm.daily_dm_limit}
                onChange={(e) =>
                  setSettingsForm((prev) => ({
                    ...prev,
                    daily_dm_limit: parseInt(e.target.value) || 1,
                  }))
                }
                className={cn(INPUT_CLASS, 'max-w-[200px]')}
              />
              <p className="text-[11px] text-gray-400 mt-0.5">
                Maximum DMs this page can send per day
              </p>
            </div>

            {/* Dedup */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settingsForm.dedup_enabled}
                  onChange={(e) =>
                    setSettingsForm((prev) => ({ ...prev, dedup_enabled: e.target.checked }))
                  }
                  className="w-4 h-4 rounded border-gray-300 text-[#1a1a2e] focus:ring-[#1a1a2e]"
                />
                <span className="text-sm text-gray-700">Deduplication</span>
              </label>
              <p className="text-[11px] text-gray-400 mt-0.5 ml-6">
                Skip users who have already been sent a DM
              </p>
            </div>

            {/* Cooldown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cooldown (seconds)
              </label>
              <input
                type="number"
                min={0}
                value={settingsForm.cooldown_seconds}
                onChange={(e) =>
                  setSettingsForm((prev) => ({
                    ...prev,
                    cooldown_seconds: parseInt(e.target.value) || 0,
                  }))
                }
                className={cn(INPUT_CLASS, 'max-w-[200px]')}
              />
              <p className="text-[11px] text-gray-400 mt-0.5">
                Wait time between DMs to avoid rate limits
              </p>
            </div>

            {/* Trigger toggles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trigger Sources
              </label>
              <div className="space-y-2">
                {([
                  { key: 'trigger_post_comments', label: 'Post comments' },
                  { key: 'trigger_story_replies', label: 'Story replies' },
                  { key: 'trigger_live_comments', label: 'Live comments' },
                  { key: 'trigger_ad_comments', label: 'Ad comments' },
                ] as const).map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settingsForm[key]}
                      onChange={(e) =>
                        setSettingsForm((prev) => ({ ...prev, [key]: e.target.checked }))
                      }
                      className="w-4 h-4 rounded border-gray-300 text-[#1a1a2e] focus:ring-[#1a1a2e]"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="pt-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveSettings}
                isLoading={settingsSaving}
              >
                Save Settings
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
